import type { CommercialBillingScheduleBreakdownItem } from "../../../../../../types/bookings";
import { isSelectOption } from "../../../../../libs/utils";
import { rentTypeOptions } from "../AddPlanComm";
import { serviceChargeTypeOptions } from "./service-charge";
import { createEmptyCommercialPlanField } from "./paymentPlanTorType";

type ResolveCommercialPlansSourceParams = {
  bookingPlans: any[];
  breakdownData: CommercialBillingScheduleBreakdownItem[];
  isTurnoverRentOnly: boolean;
};

type BuildHydratedCommercialPlansParams = {
  plansSource: any[];
  breakdownData: CommercialBillingScheduleBreakdownItem[];
  isTurnoverRentOnly: boolean;
  fallbackUnitAreaSqft: number | string | undefined;
};

/** Prefer breakdown years when API has more breakdown rows than payment-plan rows (common for TOR-only). */
export function resolveCommercialPlansSource(params: ResolveCommercialPlansSourceParams) {
  const { bookingPlans, breakdownData, isTurnoverRentOnly } = params;

  return breakdownData.length > bookingPlans.length
    ? breakdownData.map((b) => {
        const match = bookingPlans.find((p, i) => {
          const y = Number(p?.noOfYear);
          return (Number.isFinite(y) && y > 0 ? y : i + 1) === b.noOfYear;
        });
        return { ...(match ?? createEmptyCommercialPlanField(isTurnoverRentOnly)), noOfYear: b.noOfYear };
      })
    : bookingPlans;
}

export function buildHydratedCommercialPlans(params: BuildHydratedCommercialPlansParams) {
  const { plansSource, breakdownData, isTurnoverRentOnly, fallbackUnitAreaSqft } = params;

  return plansSource.map((item, idx) => {
    const planYearNumber = Number(item?.noOfYear);
    const expectedYearNumber = Number.isFinite(planYearNumber) && planYearNumber > 0 ? planYearNumber : idx + 1;
    const yearBreakdown = breakdownData.find((b) => b.noOfYear === expectedYearNumber);

    const rawTorPeriod = isSelectOption(item?.torPeriod) ? item.torPeriod.value : (item?.torPeriod ?? null);

    const fallbackUnitArea = Number(fallbackUnitAreaSqft);
    const normalizedUnitArea = Number(item?.unitAreaInSqft);
    const resolvedUnitAreaInSqft =
      item?.rentType === "areaSqft"
        ? Number.isFinite(normalizedUnitArea) && normalizedUnitArea > 0
          ? normalizedUnitArea
          : Number.isFinite(fallbackUnitArea) && fallbackUnitArea > 0
            ? fallbackUnitArea
            : undefined
        : item?.unitAreaInSqft;

    const normalizedPlan = {
      ...item,
      installments: { label: item?.installments, value: item?.installments },
      oldInstallments: item?.oldInstallments,
      rentType: rentTypeOptions.find((data) =>
        isTurnoverRentOnly ? data.value === "fixed" : data.value === item?.rentType,
      ),
      annualRent: isTurnoverRentOnly ? 0 : item?.annualRent,
      ...(resolvedUnitAreaInSqft != null
        ? { unitAreaInSqft: resolvedUnitAreaInSqft, oldUnitAreaInSqft: Number(item?.oldUnitAreaInSqft) }
        : {}),
      ...(rawTorPeriod != null ? { torPeriod: { label: rawTorPeriod, value: rawTorPeriod } } : {}),
    };

    if (!yearBreakdown) return normalizedPlan;

    const scPlan = yearBreakdown.serviceChargePlan;
    const hasServiceCharge = scPlan?.installments != null && scPlan?.rentType && scPlan?.annualRent != null;

    return {
      ...normalizedPlan,
      billingScheduleRows: yearBreakdown.commercialBillingSchedule,
      ...(yearBreakdown.commercialServiceCharge?.length > 0
        ? { serviceChargeSchedule: yearBreakdown.commercialServiceCharge }
        : {}),
      ...(hasServiceCharge
        ? {
            serviceCharge: {
              installments: { label: scPlan.installments, value: scPlan.installments },
              rentType: serviceChargeTypeOptions.find((o) => o.value === scPlan.rentType),
              annualRent: Number(scPlan.annualRent),
              chargeValue: scPlan.chargeValue != null ? Number(scPlan.chargeValue) : undefined,
            },
          }
        : {}),
    };
  });
}

/** TOR-only seed row when the plans field array is empty. */
export function createTurnoverRentOnlySeedPlan() {
  return { rentType: rentTypeOptions.find((data) => data.value === "fixed"), annualRent: 0 };
}
